import { Response } from '../../models/index';
import { DebounceUseCase } from '../DebounceUseCase';

export namespace CacheableResource {
    export interface Params {
        clearCache?: boolean;
        forceClear?: boolean;
    }
}

let CACHE: Map<Function, any>;

function initCache(force?: boolean) {
    if (!CACHE || force) {
        CACHE = new Map<Function, any>();
    }
}

export abstract class CacheableResource<
    P extends CacheableResource.Params,
    T
> extends DebounceUseCase<P | undefined, T> {
    static clearCache() {
        if (CACHE && this !== CacheableResource) {
            CACHE.delete(this);
        } else {
            initCache(true);
        }
    }

    static clearCacheFor(id: string | number) {
        if (CACHE && this !== CacheableResource) {
            const cachedMap: Map<string | number, any> | undefined = CACHE.get(this);
            if (cachedMap) {
                cachedMap.delete(id);
            }
        }
    }

    static updateCache(resource: any) {
        initCache();
        if (this !== CacheableResource) {
            CACHE.set(this, resource);
        }
    }

    static updateCacheFor(id: string | number, resource: any) {
        initCache();
        if (this !== CacheableResource) {
            try {
                const cachedMap: Map<string | number, any> = CACHE.get(this) || new Map();
                if (cachedMap && Array.isArray(cachedMap) && cachedMap.length > 0) {
                    const item = cachedMap.filter((x) => x.id === id);
                    var index = cachedMap
                        .map(function (e) {
                            return e.id;
                        })
                        .indexOf(id);
                    if (index > -1) {
                        cachedMap[index] = resource;
                    }
                } else {
                    cachedMap.set(id, resource);
                }
                CACHE.set(this, cachedMap);
            } catch (err) {
                console.log('error ', err);
                this.clearCache();
            }
        }
    }

    static getCache(): any {
        if (CACHE && CACHE.has(this)) {
            return CACHE.get(this);
        }
    }

    static getCacheFor(id: string | number): any {
        if (CACHE) {
            const cachedMap: Map<string | number, any> | undefined = CACHE.get(this);
            return cachedMap && cachedMap.get(id);
        }
    }

    execute(params?: P): Promise<T> {
        return super.execute(params);
    }

    protected debounceExecute(params?: P): Promise<T> {
        initCache();
        const ctor = this.constructor;
        const cachedValue = this.getCachedValue(params);
        if (!cachedValue || (params && (params.clearCache || params.forceClear))) {
            this.clearCachedValue(params);
            return this.doExecute(params)
                .then((resource) => {
                    this.setCachedValue(resource, params);
                    return resource;
                })
                .catch((error: Response<any>) => {
                    if (
                        cachedValue &&
                        error &&
                        error.status === 0 &&
                        (!params || !params.forceClear)
                    ) {
                        this.setCachedValue(cachedValue, params);
                        return cachedValue;
                    } else {
                        throw error;
                    }
                });
        } else {
            return Promise.resolve(cachedValue);
        }
    }

    protected getId(params?: P): string | number | undefined {
        // Returning undefined will cause CacheableResource to share the cache
        // regardless of params, instead of caching results per param identifier
        return;
    }

    protected abstract doExecute(params?: P): Promise<T>;

    protected getCachedValue(params?: P): T | undefined {
        initCache();
        const cached = CACHE.get(this.constructor);
        if (this.isCachedById(params, cached)) {
            return cached && cached.get(this.getId(params)!);
        } else {
            return cached;
        }
    }

    protected setCachedValue(value?: T, params?: P) {
        initCache();
        const ctor = this.constructor;
        let cached = CACHE.get(ctor);
        if (this.isCachedById(params, cached)) {
            cached = cached || new Map<string | number, T>();
            cached.set(this.getId(params), value);
            CACHE.set(ctor, cached);
        } else {
            CACHE.set(ctor, value);
        }
    }

    protected clearCachedValue(params?: P) {
        initCache();
        const ctor = this.constructor;
        const cached = CACHE.get(ctor);
        if (this.isCachedById(params, cached)) {
            if (cached) {
                cached.delete(this.getId(params)!);
            }
        } else {
            CACHE.delete(ctor);
        }
    }

    private isCachedById(params?: P, cached?: any): cached is Map<string | number, T> {
        return !!this.getId(params);
    }
}
